import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUser, FiMail, FiPhone, FiMapPin, FiGlobe, FiCalendar } from 'react-icons/fi';
import { useAuth } from '../../../store/auth';
import { errorToast } from '../../../utils/toasts';
import { ShareholderService } from '../../../services/ShareholderService';
import { useApi } from '../../../services/hooks/apiHook';
import { AxiosInstance } from 'axios';

interface DirectorProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  nationality: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  passportNumber: string;
  taxId: string;
}

export const DirectorProfile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const api: AxiosInstance = useApi();

  const [isLoading, setIsLoading] = useState(true);
  const lastFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const initialLanguageSyncRef = useRef(true);
  
  // Memoize config to prevent unnecessary re-renders
  const config = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Language': i18n.language.toLowerCase()
      }
    }),
    [token, i18n.language]
  );
  
  const [profileData, setProfileData] = useState<DirectorProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
    nationality: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    passportNumber: '',
    taxId: ''
  });

  // Fetch shareholder data from backend
  const fetchShareholderData = useCallback(async () => {
    if (!token || isFetchingRef.current) {
      return;
    }

    const fetchKey = `${token}|${i18n.language}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;
    isFetchingRef.current = true;

    try {
      setIsLoading(true);

      // Fetch all shareholder data in parallel
      const [meData, addressData, telephoneData, passportData] = await Promise.all([
        ShareholderService.getMe(api, config),
        ShareholderService.getMyAddress(api, config),
        ShareholderService.getMyTelephones(api, config),
        ShareholderService.getMyPassports(api, config)
      ]);

      const shareholder = meData.data;
      const address = addressData.data[0];
      const telephone = telephoneData.data[0];
      const passport = passportData.data[0];

      // Split name into first and last name
      const nameParts = shareholder.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Format phone number
      const phoneNumber = telephone 
        ? `+${telephone.DDI} ${telephone.DDD} ${telephone.number}`
        : '';

      setProfileData({
        firstName,
        lastName,
        email: shareholder.email,
        phone: phoneNumber,
        birthDate: shareholder.date_of_birth,
        nationality: shareholder.nationality,
        address: {
          street: address?.address || '',
          city: address?.city || '',
          state: address?.state || '',
          zipCode: address?.postal_code || '',
          country: address?.country || ''
        },
        passportNumber: passport?.number || '',
        taxId: shareholder.document_number || ''
      });
    } catch (error) {
      console.error('Error fetching shareholder data:', error);
      errorToast(t('messages.errorLoadingProfile') || 'Error loading profile data');
      lastFetchKeyRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [token, api, config, i18n.language, t]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchShareholderData();
      return;
    }

    const timeout = setTimeout(() => {
      void fetchShareholderData();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, i18n.language, fetchShareholderData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('labels.lblLoading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col border-b-2 pb-3">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-[#0C1FAA]">
              {t('labels.lblMyProfile') || 'My Profile'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('labels.lblManageYourPersonalInfo') || 'Manage your personal information'}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {/* Personal Information */}
        <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <FiUser className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                {t('labels.lblPersonalInformation') || 'Personal Information'}
              </h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labels.lblFirstName') || 'First Name'}
                </label>
                <input
                  type="text"
                  value={profileData.firstName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labels.lblLastName') || 'Last Name'}
                </label>
                <input
                  type="text"
                  value={profileData.lastName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiMail className="inline w-4 h-4 mr-1" />
                  {t('labels.lblEmail') || 'Email'}
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiPhone className="inline w-4 h-4 mr-1" />
                  {t('labels.lblPhone') || 'Phone'}
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiCalendar className="inline w-4 h-4 mr-1" />
                  {t('labels.lblBirthDate') || 'Birth Date'}
                </label>
                <input
                  type="date"
                  value={profileData.birthDate}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiGlobe className="inline w-4 h-4 mr-1" />
                  {t('labels.lblNationality') || 'Nationality'}
                </label>
                <input
                  type="text"
                  value={profileData.nationality}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Address Information */}
        <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <FiMapPin className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                {t('labels.lblAddress') || 'Address'}
              </h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labels.lblStreet') || 'Street'}
                </label>
                <input
                  type="text"
                  value={profileData.address.street}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labels.lblCity') || 'City'}
                  </label>
                  <input
                    type="text"
                    value={profileData.address.city}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labels.lblState') || 'State'}
                  </label>
                  <input
                    type="text"
                    value={profileData.address.state}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labels.lblZipCode') || 'Zip Code'}
                  </label>
                  <input
                    type="text"
                    value={profileData.address.zipCode}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labels.lblCountry') || 'Country'}
                  </label>
                  <input
                    type="text"
                    value={profileData.address.country}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Official Documents */}
        <section className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <FiUser className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                {t('labels.lblOfficialDocuments') || 'Official Documents'}
              </h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labels.lblPassportNumber') || 'Passport Number'}
                </label>
                <input
                  type="text"
                  value={profileData.passportNumber}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labels.lblTaxId') || 'Tax ID'}
                </label>
                <input
                  type="text"
                  value={profileData.taxId}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

    </>
  );
};
